import { type ComplaintSystemEntry } from '#app/routes/mocra+/_form+/adverse-events/form.ts'
import {
	COMPLAINT_SYSTEM_PLATFORM_OPTIONS,
	COMPLAINT_SYSTEM_USAGE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

export type AeSystemPlatformValue =
	(typeof COMPLAINT_SYSTEM_PLATFORM_OPTIONS)[number]['value']
export type AeSystemUsageValue =
	(typeof COMPLAINT_SYSTEM_USAGE_OPTIONS)[number]['value']

export type AeSystemDraft = {
	platform: AeSystemPlatformValue
	name: string
	usage: AeSystemUsageValue[]
}

export function createAeSystemDraft(
	overrides: Partial<AeSystemDraft> = {},
): AeSystemDraft {
	const defaultUsage = COMPLAINT_SYSTEM_USAGE_OPTIONS[0]?.value
	const initialUsage: AeSystemUsageValue[] = defaultUsage ? [defaultUsage] : []
	return {
		platform: COMPLAINT_SYSTEM_PLATFORM_OPTIONS[0]?.value ?? 'zendesk',
		name: '',
		usage: initialUsage,
		...overrides,
	}
}

export function sanitizeAeSystemDraft(draft: AeSystemDraft): AeSystemDraft {
	const platform = isAeSystemPlatformValue(draft.platform)
		? draft.platform
		: (COMPLAINT_SYSTEM_PLATFORM_OPTIONS[0]?.value ?? 'zendesk')
	const normalizedUsage = draft.usage
		.filter(isAeSystemUsageValue)
		.filter((value, index, array) => array.indexOf(value) === index)
	const trimmedName = draft.name.trim()
	const fallbackName =
		trimmedName.length > 0 ? trimmedName : resolvePlatformLabel(platform)
	return {
		platform,
		name: fallbackName,
		usage: normalizedUsage,
	}
}

export function collectAeSystemErrors(draft: AeSystemDraft): string[] {
	const sanitized = sanitizeAeSystemDraft(draft)
	const errors: string[] = []
	if (!sanitized.name) {
		errors.push('Provide the system or tracker name.')
	}
	if (sanitized.usage.length === 0) {
		errors.push('Select at least one workflow usage.')
	}
	return errors
}

export function aeSystemCardTitle(draft: AeSystemDraft): string {
	const sanitized = sanitizeAeSystemDraft(draft)
	return sanitized.name || 'Unnamed system'
}

export function formatAeSystemSummary(draft: AeSystemDraft): string {
	const sanitized = sanitizeAeSystemDraft(draft)
	const platformLabel = resolvePlatformLabel(sanitized.platform)
	const usageLabel = formatUsageList(sanitized.usage)
	return [platformLabel, usageLabel].filter(Boolean).join(' • ')
}

export function resolvePlatformLabel(value: AeSystemPlatformValue): string {
	return (
		COMPLAINT_SYSTEM_PLATFORM_OPTIONS.find((option) => option.value === value)
			?.label ?? value
	)
}

export function resolveUsageLabel(value: AeSystemUsageValue): string {
	return (
		COMPLAINT_SYSTEM_USAGE_OPTIONS.find((option) => option.value === value)
			?.label ?? value
	)
}

export function formatUsageList(values: AeSystemUsageValue[]): string {
	if (values.length === 0) return ''
	if (values.length === 1 && values[0]) {
		return resolveUsageLabel(values[0])
	}
	return values.map(resolveUsageLabel).join(', ')
}

export function aeSystemDraftToEntry(
	draft: AeSystemDraft,
): ComplaintSystemEntry {
	const sanitized = sanitizeAeSystemDraft(draft)
	return {
		platform: sanitized.platform,
		name: sanitized.name,
		usage: sanitized.usage,
	}
}

export function aeSystemEntryToDraft(
	entry: ComplaintSystemEntry,
): AeSystemDraft {
	const platform = isAeSystemPlatformValue(entry.platform)
		? entry.platform
		: (COMPLAINT_SYSTEM_PLATFORM_OPTIONS[0]?.value ?? 'zendesk')
	const usage = Array.isArray(entry.usage)
		? entry.usage.filter(isAeSystemUsageValue)
		: []
	return sanitizeAeSystemDraft({
		platform,
		name: entry.name,
		usage,
	})
}

export function isAeSystemPlatformValue(
	value: unknown,
): value is AeSystemPlatformValue {
	return COMPLAINT_SYSTEM_PLATFORM_OPTIONS.some(
		(option) => option.value === value,
	)
}

export function isAeSystemUsageValue(
	value: unknown,
): value is AeSystemUsageValue {
	return COMPLAINT_SYSTEM_USAGE_OPTIONS.some((option) => option.value === value)
}
